import type { Order } from '../../../types'
import { CRYPTO_ASSETS, NETWORKS } from '../../../lib/constants'
import { formatMYR, formatCrypto, formatDate, copyToClipboard } from '../../../lib/utils'
import { cx, text, ui, statusStyles, focus } from '../../../lib/ui-primitives'
import { Button, Card, Input } from '../../primitives'
import { XIcon, CopyIcon, ExternalLinkIcon } from '../../icons'

// ============================================
// ORDER DETAIL PANEL
// Full order details with actions
// ============================================

export interface OrderDetailPanelProps {
  order: Order | null
  onClose: () => void
  onApprove: (order: Order) => Promise<void>
  onReject: (order: Order) => Promise<void>
  onComplete: (order: Order, txHash: string) => Promise<void>
  txHash: string
  onTxHashChange: (value: string) => void
  actionLoading?: boolean
}

/**
 * OrderDetailPanel - Full order details with admin actions
 *
 * Features:
 * - Order summary
 * - Customer details
 * - Wallet address with copy
 * - Action buttons (approve/reject/complete)
 * - Transaction hash input
 */
export function OrderDetailPanel({
  order,
  onClose,
  onApprove,
  onReject,
  onComplete,
  txHash,
  onTxHashChange,
  actionLoading = false,
}: OrderDetailPanelProps) {
  // Empty state
  if (!order) {
    return (
      <div className="bg-white/[0.02] rounded-xl border border-white/10 p-8 text-center">
        <div className="text-4xl mb-3" aria-hidden="true">📋</div>
        <p className={text.tertiary}>Select an order to view details</p>
      </div>
    )
  }

  const asset = CRYPTO_ASSETS[order.crypto]
  const network = NETWORKS[order.network]
  const statusStyle = statusStyles[order.status] || statusStyles.pending

  return (
    <Card padding="md" className="sticky top-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={text.h4}>Order Details</h3>
        <button
          onClick={onClose}
          className={cx(
            'p-1.5 rounded-lg hover:bg-white/10 transition-colors',
            focus.ring
          )}
          aria-label="Close panel"
        >
          <XIcon />
        </button>
      </div>

      <div className="space-y-4">
        {/* Order ID + Status */}
        <div className="flex items-center justify-between">
          <span className={cx('text-lg sm:text-xl', text.mono)}>{order.id}</span>
          <span className={cx('px-3 py-1 rounded-full text-sm font-medium', statusStyle)}>
            {order.status.toUpperCase()}
          </span>
        </div>

        {/* Amount Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
          <DetailItem label="Amount Paid" value={formatMYR(order.amountMYR)} />
          <DetailItem
            label="Crypto Amount"
            value={`${formatCrypto(order.amountCrypto, 6)} ${order.crypto}`}
            valueColor={asset?.color}
            mono
          />
          <DetailItem label="Network" value={order.network} />
          <DetailItem label="Rate" value={formatMYR(order.rate)} />
        </div>

        {/* Customer Section */}
        <Section title="Customer">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={text.tertiary}>Name</span>
              <span className="text-white">{order.customer.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={text.tertiary}>{order.customer.contactType}</span>
              <CopyableValue value={order.customer.contact} />
            </div>
          </div>
        </Section>

        {/* Wallet Address */}
        <Section title="Wallet Address">
          <div className="flex items-center gap-2">
            <code className={cx(
              'flex-1 text-xs bg-kiosk-bg p-2 rounded-lg break-all border border-white/5',
              text.mono
            )}>
              {order.customer.walletAddress}
            </code>
            <CopyButton value={order.customer.walletAddress} />
          </div>
        </Section>

        {/* Payment Reference */}
        {order.paymentRef && (
          <Section title="Payment Reference">
            <span className={cx('text-white', text.mono)}>{order.paymentRef}</span>
          </Section>
        )}

        {/* Transaction Hash (if completed) */}
        {order.txHash && network && (
          <Section title="Transaction Hash">
            <a
              href={`${network.explorer}${order.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cx(
                'text-indigo-400 text-sm hover:underline inline-flex items-center gap-1',
                text.mono
              )}
            >
              {order.txHash.slice(0, 20)}...
              <ExternalLinkIcon />
            </a>
          </Section>
        )}

        {/* Timestamps */}
        <div className={cx('text-xs', text.disabled)}>
          Created: {formatDate(order.createdAt)}
          {order.updatedAt !== order.createdAt && (
            <> • Updated: {formatDate(order.updatedAt)}</>
          )}
        </div>

        {/* Actions: Pending Order */}
        {order.status === 'pending' && (
          <ActionSection>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="primary"
                fullWidth
                onClick={() => onApprove(order)}
                disabled={actionLoading}
                bgColor="#10B981"
              >
                ✓ Approve
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={() => onReject(order)}
                disabled={actionLoading}
              >
                ✗ Reject
              </Button>
            </div>
          </ActionSection>
        )}

        {/* Actions: Approved Order - Complete with TX Hash */}
        {order.status === 'approved' && (
          <ActionSection>
            <p className={cx('text-sm mb-3', text.tertiary)}>
              Mark as completed with TX hash:
            </p>
            <div className="flex gap-2">
              <Input
                value={txHash}
                onChange={(e) => onTxHashChange(e.target.value)}
                placeholder="Transaction hash..."
                className="flex-1 font-mono text-sm"
              />
              <Button
                variant="primary"
                onClick={() => onComplete(order, txHash)}
                disabled={actionLoading || !txHash.trim()}
              >
                Complete
              </Button>
            </div>
          </ActionSection>
        )}
      </div>
    </Card>
  )
}

// ============================================
// HELPER COMPONENTS
// ============================================

interface DetailItemProps {
  label: string
  value: string
  valueColor?: string
  mono?: boolean
}

function DetailItem({ label, value, valueColor, mono }: DetailItemProps) {
  return (
    <div>
      <div className={cx('text-xs mb-1', text.tertiary)}>{label}</div>
      <div
        className={cx('text-white font-medium', mono && text.mono)}
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

function Section({ title, children }: SectionProps) {
  return (
    <div className={cx('pt-4', ui.divider)}>
      <div className={cx('text-xs mb-2', text.tertiary)}>{title}</div>
      {children}
    </div>
  )
}

function ActionSection({ children }: { children: React.ReactNode }) {
  return (
    <div className={cx('pt-4 space-y-3', ui.divider)}>
      {children}
    </div>
  )
}

interface CopyableValueProps {
  value: string
}

function CopyableValue({ value }: CopyableValueProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-white">{value}</span>
      <CopyButton value={value} />
    </div>
  )
}

function CopyButton({ value }: { value: string }) {
  return (
    <button
      onClick={() => copyToClipboard(value)}
      className={cx(
        'p-1.5 rounded-lg text-content-tertiary hover:text-white hover:bg-white/10 transition-colors shrink-0',
        focus.ring
      )}
      aria-label={`Copy ${value}`}
    >
      <CopyIcon />
    </button>
  )
}
